import { Plus } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { TagBadge } from '@/components/shared/TagBadge';
import { t } from '@/i18n';
import type { Tag as TagType } from '@ip/shared-types';

interface InvoiceTagsEditorProps {
  tags: TagType[];
  selectedTagIds: string[];
  onToggleTag: (tagId: string, checked: boolean) => void;
  disabled?: boolean;
}

/**
 * Task A3: inline tag row for the invoice-detail header — the same
 * click-to-open checkbox picker the invoice lists use, so tags are finally
 * visible AND editable straight from the detail view (cost and sales alike).
 * The whole row is the popover trigger; a dashed "+ Dodaj tag" chip renders
 * next to existing badges so a fully-tagged invoice is still editable.
 */
export function InvoiceTagsEditor({ tags, selectedTagIds, onToggleTag, disabled }: InvoiceTagsEditorProps) {
  const getTagById = (id: string) => tags.find((tag) => tag.id === id);

  return (
    <Popover>
      <PopoverTrigger asChild disabled={disabled}>
        <div
          className="flex min-h-[22px] cursor-pointer flex-wrap items-center gap-1"
          role="button"
          aria-label={t('invoices.tagsEditor.addTag')}
        >
          {selectedTagIds.map((id) => {
            const tag = getTagById(id);
            return tag ? <TagBadge key={id} name={tag.name} color={tag.color} /> : null;
          })}
          <span className="text-muted-foreground hover:text-foreground inline-flex items-center gap-0.5 rounded-md border border-dashed px-2 py-0.5 text-xs">
            <Plus className="size-3" /> {t('invoices.tagsEditor.addTag')}
          </span>
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-56 space-y-2" align="start">
        {tags.map((tag) => (
          <Label key={tag.id} className="flex items-center gap-2 py-0.5 font-normal">
            <Checkbox
              checked={selectedTagIds.includes(tag.id)}
              disabled={disabled}
              onCheckedChange={(checked) => onToggleTag(tag.id, checked === true)}
            />
            <TagBadge name={tag.name} color={tag.color} />
          </Label>
        ))}
        {tags.length === 0 && (
          <p className="text-muted-foreground text-sm">{t('invoices.tagsEditor.noTags')}</p>
        )}
      </PopoverContent>
    </Popover>
  );
}
